import React, { useState, useEffect } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { getStoreImageUrl } from '../../../util/sellerImages';

import css from './SearchMapStoreInfoCard.module.css';

const MAP_VARIANT_DEALER_LOCATOR = 'dealerLocator';

const resolveStoreImageUrl = (store, mapVariant) => {
  return getStoreImageUrl(store);
};

const resolveStoreLocation = store => {
  const publicData = store?.attributes?.profile?.publicData || {};
  const address = publicData?.address;

  if (address?.city && address?.state) {
    return `${address.city}, ${address.state}`;
  }

  const placeName = publicData?.storedAddress?.place_name || '';
  const segments = placeName
    .split(',')
    .map(segment => segment.trim())
    .filter(Boolean);

  return segments.slice(0, 2).join(', ');
};

// ListingCard is the listing info without overlayview or carousel controls
const ListingCard = props => {
  const { className, clickHandler, intl, isInCarousel, listing, urlToListing, config, mapVariant } = props;
  const store = listing?.store || {};
  const { attributes } = store;
  const { businessName } = attributes?.profile?.publicData || {};
  const locationLabel = resolveStoreLocation(store);

  const [image, setImage] = useState(null);
  const [fetchingImage, setFetchingImage] = useState(false);

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  useEffect(() => {
    const imageUrl = resolveStoreImageUrl(store, mapVariant);

    if (imageUrl && image !== imageUrl && !fetchingImage) {
      setImage(imageUrl);
    } else if (!imageUrl && image) {
      setImage(null);
    }
  }, [store, mapVariant, image, fetchingImage]);

  const shouldShowProductCount =
    mapVariant !== MAP_VARIANT_DEALER_LOCATOR && typeof listing.totalListings === 'number';

  return (
    <a
      alt={businessName}
      className={classes}
      href={urlToListing}
      onClick={e => {
        e.preventDefault();
        // Use clickHandler from props to call internal router
        clickHandler(listing);
      }}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
      >
        <div className={css.storeProfilePicture}>
          <img
            src={image || '/static/footer-logo.png'}
            className={classNames({
              [css.noImage]: !image,
            })}
          />
        </div>
        <div className={classNames(css.info, { [css.borderRadiusInheritBottom]: !isInCarousel })}>
          <div className={css.name}>{businessName}</div>
          {locationLabel ? <div className={css.location}>{locationLabel}</div> : null}
          {shouldShowProductCount ? (
            <div className={css.products}>Products: {listing.totalListings}</div>
          ) : null}
        </div>
      </div>
    </a>
  );
};

ListingCard.defaultProps = {
  className: null,
};

ListingCard.propTypes = {
  className: string,
  listing: propTypes.listing.isRequired,
  clickHandler: func.isRequired,
  intl: intlShape.isRequired,
  isInCarousel: bool.isRequired,
  mapVariant: string,
};

const SearchMapInfoCard = props => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const {
    className,
    rootClassName,
    intl,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
    mapVariant,
  } = props;
  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;

  const classes = classNames(rootClassName || css.root, className);
  const caretClass = classNames(css.caret, { [css.caretWithCarousel]: hasCarousel });

  return (
    <div className={classes}>
      <div className={css.caretShadow} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
        mapVariant={mapVariant}
      />
      {hasCarousel ? (
        <div className={classNames(css.paginationInfo, css.borderRadiusInheritBottom)}>
          <button
            className={css.paginationPrev}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length - 1) % listings.length
              );
            }}
          />
          <div className={css.paginationPage}>
            {`${currentListingIndex + 1}/${listings.length}`}
          </div>
          <button
            className={css.paginationNext}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length + 1) % listings.length
              );
            }}
          />
        </div>
      ) : null}
      <div className={caretClass} />
    </div>
  );
};

SearchMapInfoCard.defaultProps = {
  className: null,
  rootClassName: null,
};

SearchMapInfoCard.propTypes = {
  className: string,
  rootClassName: string,
  listings: arrayOf(propTypes.listing).isRequired,
  onListingInfoCardClicked: func.isRequired,
  createURLToListing: func.isRequired,
  config: object.isRequired,
  mapVariant: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(injectIntl)(SearchMapInfoCard);
